import { CircleDollarSign, Handshake, Info } from 'lucide-react';
import { SectionIcon } from '../ui/SectionIcon.js';
import { useId, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { PortfolioCompanyDetail, PortfolioRound } from '@nemo/api';
import { STAGE_LABELS, dealroomCompanyUrl, toCsv } from '@nemo/core/model';
import { useLocation } from 'wouter';
import { download, today } from '../grid/csv.js';
import { DockedPane, PaneBack, PaneClose } from '../pane/DockedPane.js';
import { PaneLinks, type OutsideLink } from '../pane/PaneLinks.js';
import { InlineText, PaneRow, PickField, hostOf } from '../pane/fields.js';
import { useTRPC } from '../trpc.js';
import { ConfirmModal } from '../ui/ConfirmModal.js';
import { formatDay, formatEur } from '../ui/format.js';
import { SiteIcon, siteOf } from '../ui/SiteIcon.js';
import { Menu } from '../ui/Menu.js';
import { useToast } from '../ui/Toast.js';
import { SuggestDrawer } from '../suggest/SuggestDrawer.js';
import { AddRound } from './AddRound.js';
import {
  affinityOrganizationUrl,
  employeesText,
  lastRaiseText,
  roundAmount,
  roundTitle,
  sourcesText,
} from './format.js';
import { useProjectAFunds } from './useProjectAFunds.js';
import { useAssistantFocus } from '../assistant/focus.js';
import { invalidateFundRows } from '../grid/invalidateFundRows.js';
import { useFundNameSearch } from '../grid/useFundNameSearch.js';

export interface PortfolioPaneProps {
  companyId: string;
  onClose: () => void;
  onStep: (delta: 1 | -1) => void;
}

/**
 * A portfolio company in the docked pane every record opens in: who it is
 * and who holds it, then who sat beside Project A in it, then the rounds it
 * raised with every investor in each. A co-investor links into the funds
 * pane, because that is where the question "who do we know there" is
 * answered.
 */
export function PortfolioPane(props: PortfolioPaneProps): React.JSX.Element {
  const trpc = useTRPC();
  const company = useQuery(trpc.portfolio.get.queryOptions({ id: props.companyId }));
  useAssistantFocus('companyId', props.companyId);

  return (
    <DockedPane
      label={company.data?.name ?? 'Portfolio company'}
      onClose={props.onClose}
      onStep={props.onStep}
    >
      {company.data === undefined ? (
        <header className="pane-head">
          <PaneBack onClose={props.onClose} />
          <p className="subtle">{company.isError ? company.error.message : 'Loading…'}</p>
        </header>
      ) : (
        <CompanyBody company={company.data} onClose={props.onClose} />
      )}
    </DockedPane>
  );
}

function CompanyBody(props: {
  company: PortfolioCompanyDetail;
  onClose: () => void;
}): React.JSX.Element {
  const { company } = props;
  const trpc = useTRPC();
  const toast = useToast();
  const queryClient = useQueryClient();
  const projectAFunds = useProjectAFunds();
  const [otherFund, setOtherFund] = useState('');
  const [creating, setCreating] = useState<string | null>(null);
  const [suggesting, setSuggesting] = useState(false);

  const refresh = (): void => {
    void queryClient.invalidateQueries({ queryKey: trpc.portfolio.list.queryKey() });
    void queryClient.invalidateQueries({
      queryKey: trpc.portfolio.get.queryKey({ id: company.id }),
    });
    invalidateFundRows(queryClient, trpc);
    void queryClient.invalidateQueries({ queryKey: trpc.projectAFunds.list.queryKey() });
  };
  const upsert = useMutation(
    trpc.portfolio.upsert.mutationOptions({
      onSuccess: refresh,
      onError: (error: { message: string }) => toast(error.message),
    }),
  );

  const dealroomUrl = dealroomCompanyUrl(company.dealroomPath);
  const links = useQuery(trpc.portfolio.links.queryOptions());
  const affinityUrl = affinityOrganizationUrl(
    links.data?.affinityWebBase ?? null,
    company.affinityOrganizationId,
  );
  const outside: OutsideLink[] = [];
  if (company.website !== null) {
    outside.push({
      label: 'Website',
      href: company.website,
      title: hostOf(company.website),
      aria: 'on the web',
    });
  }
  if (dealroomUrl !== null) {
    outside.push({ label: 'Dealroom', href: dealroomUrl, title: dealroomUrl, aria: 'on Dealroom' });
  }
  if (affinityUrl !== null) {
    outside.push({ label: 'Affinity', href: affinityUrl, title: affinityUrl, aria: 'in Affinity' });
  }
  // The words the fund accounting uses, and anything it has used since.
  const options = useQuery(trpc.coInvestments.options.queryOptions());
  const known = options.data?.statuses ?? [];
  const statuses = known.includes(company.status) ? known : [...known, company.status];
  const holdingOptions = projectAFunds.codes.map((code) => ({ id: code, label: code }));

  const addHolding = (): void => {
    const code = otherFund.trim();
    setOtherFund('');
    if (code === '' || company.holdingFunds.includes(code)) return;
    if (projectAFunds.isKnown(code)) {
      upsert.mutate({ id: company.id, holdingFunds: [...company.holdingFunds, code] });
    } else {
      setCreating(code);
    }
  };

  return (
    <>
      {suggesting && (
        <SuggestDrawer
          listId={null}
          companyId={company.id}
          title={`Suggest follow-on investors for ${company.name}`}
          onClose={() => setSuggesting(false)}
        />
      )}
      <header className="pane-head">
        <PaneBack onClose={props.onClose} />
        <div className="pane-head-line">
          <SiteIcon site={siteOf(company.website)} name={company.name} size={28} />
          <InlineText
            className="pane-name"
            label="Company name"
            value={company.name}
            display={company.name}
            onCommit={(name) => {
              if (name.trim() !== '' && name.trim() !== company.name) {
                upsert.mutate({ id: company.id, name: name.trim() });
              }
            }}
          />
          <span className="pane-head-actions">
            <Menu
              label={`More for ${company.name}`}
              items={[
                {
                  label: 'Suggest follow-on investors',
                  onSelect: () => setSuggesting(true),
                },
                {
                  label: 'Copy link',
                  onSelect: () => {
                    const link = `${window.location.origin}/portfolio?company=${company.id}`;
                    void navigator.clipboard
                      .writeText(link)
                      .then(() => toast(`Copied the link to ${company.name}.`, { tone: 'info' }))
                      .catch(() => toast('Could not reach the clipboard.'));
                  },
                },
                {
                  label: 'Export rounds',
                  onSelect: () =>
                    download(`${slugOf(company.name)}-rounds-${today()}.csv`, roundsCsv(company)),
                },
              ]}
            />
            <PaneClose onClose={props.onClose} />
          </span>
        </div>

        <PaneLinks name={company.name} links={outside} />

        <dl className="pane-facts">
          <div className="pane-fact">
            <dt className="pane-fact-label">Status</dt>
            <dd className="pane-fact-value">
              <PickField
                label={`How ${company.name} is doing`}
                options={statuses.map((status) => ({ id: status, label: status }))}
                selectedIds={[company.status]}
                onCommit={([status]) => {
                  if (status !== undefined && status !== company.status) {
                    upsert.mutate({ id: company.id, status });
                  }
                }}
              >
                {company.status}
              </PickField>
            </dd>
          </div>
          <div className="pane-fact">
            <dt className="pane-fact-label">Co-investors</dt>
            <dd className="pane-fact-value">{company.coInvestors.length}</dd>
          </div>
          <div className="pane-fact">
            <dt className="pane-fact-label">Rounds</dt>
            <dd className="pane-fact-value">{company.rounds.length}</dd>
          </div>
          <OverviewFact label="HQ" value={hqText(company)} />
          <OverviewFact label="Entry" value={entryText(company)} />
          <OverviewFact label="Category" value={company.overview.category} />
          <OverviewFact
            label="Source"
            value={company.sources.length === 0 ? null : sourcesText(company)}
          />
          <OverviewFact label="Last raise" value={lastRaiseText(company)} />
          <OverviewFact label="Stage" value={company.growthStage} />
          <OverviewFact
            label="Total raised"
            value={company.totalFundingEur === null ? null : formatEur(company.totalFundingEur)}
          />
          <OverviewFact label="Employees" value={employeesText(company)} />
        </dl>

        <div className="pane-sector-tags" aria-label="Sectors">
          {company.sectorTagDetails.length === 0 ? (
            <span className="pane-fact-empty">No sector tags yet</span>
          ) : (
            company.sectorTagDetails
              .filter((tag) => company.sectorTags.includes(tag.tag))
              .map((tag) => (
                <span
                  className="owner-chip"
                  key={tag.tag}
                  title={`${Math.round(tag.confidence * 100)}% sure: “${tag.evidence}”`}
                >
                  {tag.tag}
                </span>
              ))
          )}
        </div>

        <div className="pane-holdings">
          <PickField
            multiple
            className="pane-owners"
            label={`Project A funds holding ${company.name}`}
            options={holdingOptions}
            selectedIds={company.holdingFunds}
            onCommit={(codes) => upsert.mutate({ id: company.id, holdingFunds: [...codes] })}
          >
            <span className="pane-owners-label">Held by</span>
            <span className="pane-owner-chips">
              {company.holdings.length === 0 ? (
                <span className="pane-placeholder">Add a Project A fund…</span>
              ) : (
                company.holdings.map((holding) => (
                  <span className="owner-chip" key={holding.code} title={holding.name ?? undefined}>
                    {holding.code}
                  </span>
                ))
              )}
            </span>
          </PickField>
          <input
            className="coinvest-other"
            aria-label="Another Project A fund"
            placeholder="Other fund"
            value={otherFund}
            onChange={(event) => setOtherFund(event.target.value)}
            onKeyDown={(event) => {
              if (event.key !== 'Enter') return;
              event.preventDefault();
              addHolding();
            }}
          />
        </div>
      </header>

      <CoInvestorsSection company={company} onChanged={refresh} />
      <RoundsSection company={company} onChanged={refresh} />
      {company.dealroomLinkCandidates.length > 0 && (
        <DealroomCandidatesSection company={company} onChanged={refresh} />
      )}
      <DetailsSection company={company} />

      {creating !== null && (
        <ConfirmModal
          title={`Add ${creating} as a Project A fund?`}
          body="A company can only be held by one of Project A's own funds. Name it properly on the Project A funds tab."
          confirmLabel="Add fund"
          onConfirm={() => {
            const code = creating;
            setCreating(null);
            void projectAFunds.create(code).then((created) => {
              if (created) {
                upsert.mutate({ id: company.id, holdingFunds: [...company.holdingFunds, code] });
              }
            });
          }}
          onCancel={() => setCreating(null)}
        />
      )}
    </>
  );
}

/**
 * Who sat beside Project A in the company. Each is a way into the funds pane,
 * which is where "who do we know there" lives; a line added by hand is gone
 * for good when removed, one from the fund accounting comes back with the
 * next import, and the confirm says which.
 */
function CoInvestorsSection(props: {
  company: PortfolioCompanyDetail;
  onChanged: () => void;
}): React.JSX.Element {
  const { company } = props;
  const trpc = useTRPC();
  const toast = useToast();
  const [, navigate] = useLocation();
  const [adding, setAdding] = useState(false);
  const [removing, setRemoving] = useState<PortfolioCompanyDetail['coInvestors'][number] | null>(
    null,
  );

  const remove = useMutation(
    trpc.coInvestments.remove.mutationOptions({
      onSuccess: () => {
        props.onChanged();
        toast(`${removing?.fundName ?? 'That fund'} is no longer recorded in ${company.name}.`, {
          tone: 'info',
        });
        setRemoving(null);
      },
      onError: (error: { message: string }) => toast(error.message),
    }),
  );

  return (
    <section className="pane-section">
      <h3 className="pane-section-title">
        <SectionIcon icon={Handshake} />
        Co-investors
      </h3>
      {company.coInvestors.length === 0 && (
        <p className="subtle">Nobody is recorded beside Project A here.</p>
      )}
      <div className="pane-list">
        {company.coInvestors.map((row) => (
          <p className="round-line coinvest-row" key={row.fundId ?? row.fundName}>
            {row.fundId === null ? (
              <span className="round-company">{row.fundName}</span>
            ) : (
              <a
                className="round-company"
                href={`/?fund=${row.fundId}`}
                onClick={(event) => {
                  event.preventDefault();
                  navigate(`/?fund=${row.fundId}`);
                }}
              >
                {row.fundName}
              </a>
            )}
            <span className="spacer" />
            <span className="round-part">{row.manual ? 'added here' : 'from Quantium'}</span>
            {row.fundId !== null && (
              <button
                type="button"
                className="icon-button danger coinvest-remove"
                aria-label={`Remove ${row.fundName} from ${company.name}`}
                title={`Remove ${row.fundName} from ${company.name}`}
                onClick={() => setRemoving(row)}
              >
                ✕
              </button>
            )}
          </p>
        ))}
      </div>

      {adding ? (
        <AddCoInvestor
          company={company}
          onDone={() => {
            props.onChanged();
            setAdding(false);
          }}
          onClose={() => setAdding(false)}
        />
      ) : (
        <button className="pane-add-link" type="button" onClick={() => setAdding(true)}>
          Add co-investor
        </button>
      )}

      {removing !== null && removing.fundId !== null && (
        <ConfirmModal
          title={`Remove ${removing.fundName} from ${company.name}?`}
          body={
            removing.manual
              ? 'Somebody added this here, so nothing will bring it back.'
              : "Quantium's export lists this pair, so the next import will bring it back."
          }
          confirmLabel="Remove"
          danger
          onConfirm={() => {
            if (removing.fundId === null) return;
            remove.mutate({ fundId: removing.fundId, portfolioCompanyId: company.id });
          }}
          onCancel={() => setRemoving(null)}
        />
      )}
    </section>
  );
}

/**
 * A fund beside Project A in this company. Picking a fund on the list goes
 * through the same add the fund pane's "Add co-investment" does, the company
 * already filled in; a name the list does not have is matched the way the
 * import matches and added as a fund when nothing answers.
 */
function AddCoInvestor(props: {
  company: PortfolioCompanyDetail;
  onDone: () => void;
  onClose: () => void;
}): React.JSX.Element {
  const { company } = props;
  const trpc = useTRPC();
  const toast = useToast();
  const listId = useId();
  const [name, setName] = useState('');
  const search = useFundNameSearch(name);
  const known = search.matches.find(
    (fund) => fund.name.toLowerCase() === name.trim().toLowerCase(),
  );
  const onError = (error: { message: string }): void => toast(error.message);
  const onSuccess = (): void => {
    toast(`${name.trim()} is recorded as a co-investor in ${company.name}.`, { tone: 'info' });
    props.onDone();
  };
  const addKnown = useMutation(trpc.coInvestments.add.mutationOptions({ onSuccess, onError }));
  const addNamed = useMutation(
    trpc.portfolio.setCoInvestors.mutationOptions({ onSuccess, onError }),
  );
  const busy = addKnown.isPending || addNamed.isPending;

  return (
    <form
      className="log-contact coinvest-form"
      onSubmit={(event) => {
        event.preventDefault();
        const typed = name.trim();
        if (typed === '' || !search.settled) return;
        if (known !== undefined) {
          addKnown.mutate({
            fundId: known.id,
            company: company.name,
            status: company.status,
            holdingFunds: company.holdingFunds,
          });
        } else {
          addNamed.mutate({
            portfolioCompanyId: company.id,
            coInvestors: [...company.coInvestors.map((row) => row.fundName), typed],
          });
        }
      }}
      onKeyDown={(event) => {
        if (event.key !== 'Escape') return;
        event.stopPropagation();
        props.onClose();
      }}
    >
      <input
        autoFocus
        list={listId}
        aria-label="Co-investor"
        placeholder="Fund"
        value={name}
        onChange={(event) => setName(event.target.value)}
      />
      <datalist id={listId}>
        {search.matches.map((fund) => (
          <option key={fund.id} value={fund.name} />
        ))}
      </datalist>
      {name.trim() !== '' && search.settled && known === undefined && (
        <p className="subtle coinvest-hint">
          No fund by that name: it is matched the way the Quantium import matches, and added as a
          fund if nothing answers.
        </p>
      )}
      <div className="log-contact-row">
        <button
          className="button small primary"
          type="submit"
          disabled={busy || name.trim() === '' || !search.settled}
        >
          Add
        </button>
        <button className="button small" type="button" onClick={props.onClose}>
          Cancel
        </button>
      </div>
    </form>
  );
}

function RoundsSection(props: {
  company: PortfolioCompanyDetail;
  onChanged: () => void;
}): React.JSX.Element {
  const { company } = props;
  const trpc = useTRPC();
  const toast = useToast();
  const [adding, setAdding] = useState(false);
  const [removing, setRemoving] = useState<PortfolioRound | null>(null);

  const remove = useMutation(
    trpc.portfolio.removeRound.mutationOptions({
      onSuccess: () => {
        props.onChanged();
        toast(`${removing === null ? 'The round' : roundTitle(removing)} is removed.`, {
          tone: 'info',
        });
        setRemoving(null);
      },
      onError: (error: { message: string }) => toast(error.message),
    }),
  );

  return (
    <section className="pane-section">
      <h3 className="pane-section-title">
        <SectionIcon icon={CircleDollarSign} />
        Rounds
      </h3>
      {company.rounds.length === 0 && <p className="subtle">No rounds are recorded here.</p>}
      <div className="pane-list">
        {company.rounds.map((round) => (
          <div className="round" key={round.id}>
            <p className="round-line coinvest-row">
              <span className="round-company">{roundTitle(round) || 'Round'}</span>
              <span className="round-part">
                {round.source === 'manual' ? 'added here' : 'Dealroom'}
              </span>
              <span className="spacer" />
              <span className="amount">{roundAmount(round)}</span>
              {round.source === 'manual' && (
                <button
                  type="button"
                  className="icon-button danger coinvest-remove"
                  aria-label={`Remove ${roundTitle(round)}`}
                  title={`Remove ${roundTitle(round)}`}
                  onClick={() => setRemoving(round)}
                >
                  ✕
                </button>
              )}
            </p>
            {round.participants.length > 0 && (
              <p className="round-sub">{participantsText(round)}</p>
            )}
          </div>
        ))}
      </div>

      {adding ? (
        <AddRound
          company={company}
          onDone={() => {
            props.onChanged();
            setAdding(false);
          }}
          onClose={() => setAdding(false)}
        />
      ) : (
        <button className="pane-add-link" type="button" onClick={() => setAdding(true)}>
          Add round
        </button>
      )}

      {removing !== null && (
        <ConfirmModal
          title={`Remove ${roundTitle(removing)} from ${company.name}?`}
          body="The funds only this round named stop being co-investors here."
          confirmLabel="Remove"
          danger
          onConfirm={() => remove.mutate({ investmentId: removing.id })}
          onCancel={() => setRemoving(null)}
        />
      )}
    </section>
  );
}

function DetailsSection({ company }: { company: PortfolioCompanyDetail }): React.JSX.Element {
  const imported = formatDay(company.quantiumImportedAt);
  const profile = company.dealroomProfile;
  return (
    <details className="pane-section pane-details">
      <summary className="pane-section-title">
        <SectionIcon icon={Info} />
        Details
      </summary>
      <PaneRow label="Founders">
        {company.overview.founderNames.length === 0 ? (
          <span className="subtle">Not on the overview sheet</span>
        ) : (
          company.overview.founderNames.join(', ')
        )}
      </PaneRow>
      <PaneRow label="Aliases">
        {company.aliases.length === 0 ? (
          <span className="subtle">None</span>
        ) : (
          company.aliases.join(', ')
        )}
      </PaneRow>
      <PaneRow label="Quantium">
        {imported === null
          ? 'Added here, not in an export'
          : `${company.status}, imported ${imported}`}
      </PaneRow>
      <PaneRow label="Dealroom link">
        {company.dealroomLinkNote ?? (company.companyId === null ? 'Not linked yet' : 'Linked')}
      </PaneRow>
      {profile !== null && (
        <>
          <PaneRow label="Dealroom tags">{listText(profile.tags)}</PaneRow>
          <PaneRow label="Business model">{listText(profile.businessModels)}</PaneRow>
          <PaneRow label="Revenue model">{listText(profile.revenueModels)}</PaneRow>
          <PaneRow label="Sells to">{listText(profile.clientFocus)}</PaneRow>
          <PaneRow label="Launched">
            {profile.launchYear ?? <span className="subtle">—</span>}
          </PaneRow>
          <PaneRow label="Investors">
            {profile.investors.length === 0 ? (
              <span className="subtle">—</span>
            ) : (
              `${profile.investors.join(', ')}${
                profile.investorCount !== null && profile.investorCount > profile.investors.length
                  ? ` and ${profile.investorCount - profile.investors.length} more`
                  : ''
              }`
            )}
          </PaneRow>
          <PaneRow label="Dealroom read">{formatDay(profile.readAt)}</PaneRow>
        </>
      )}
    </details>
  );
}

function listText(values: readonly string[]): React.ReactNode {
  return values.length === 0 ? <span className="subtle">—</span> : values.join(', ');
}

/**
 * The Dealroom companies the link could not choose between: several share the
 * name and nothing on Dealroom says which one Project A backed. A person who
 * knows picks one; the next enrichment run reads its profile and rounds.
 */
function DealroomCandidatesSection(props: {
  company: PortfolioCompanyDetail;
  onChanged: () => void;
}): React.JSX.Element {
  const { company } = props;
  const trpc = useTRPC();
  const toast = useToast();
  const link = useMutation(
    trpc.portfolio.linkDealroom.mutationOptions({
      onSuccess: () => {
        toast(`${company.name} linked to Dealroom.`);
        props.onChanged();
      },
      onError: (error: { message: string }) => toast(error.message),
    }),
  );
  return (
    <section className="pane-section">
      <h3 className="pane-section-title">
        <SectionIcon icon={Info} />
        Which one on Dealroom?
      </h3>
      <div className="pane-list">
        {company.dealroomLinkCandidates.map((candidate) => {
          const url = dealroomCompanyUrl(candidate.path);
          const facts = [
            candidate.website === null ? null : hostOf(candidate.website),
            candidate.hqCountries.join(', ') || null,
            candidate.launchYear === null ? null : `launched ${candidate.launchYear}`,
            candidate.projectAInvestor ? 'Project A among its investors' : null,
          ].filter((fact): fact is string => fact !== null);
          return (
            <div className="round" key={candidate.dealroomId}>
              <p className="round-line coinvest-row">
                <span className="round-company">
                  {url === null ? (
                    candidate.name
                  ) : (
                    <a href={url} target="_blank" rel="noreferrer">
                      {candidate.name}
                    </a>
                  )}
                </span>
                <span className="spacer" />
                <button
                  className="button small"
                  type="button"
                  disabled={link.isPending}
                  onClick={() =>
                    link.mutate({
                      portfolioCompanyId: company.id,
                      dealroomId: candidate.dealroomId,
                    })
                  }
                >
                  This one
                </button>
              </p>
              {(facts.length > 0 || candidate.tagline !== null) && (
                <p className="subtle">
                  {[...facts, candidate.tagline].filter((part) => part !== null).join(' · ')}
                </p>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}

/** A fact the overview sheet supplies, read-only: the sheet is where it is changed. */
function OverviewFact(props: { label: string; value: string | null }): React.JSX.Element {
  return (
    <div className="pane-fact">
      <dt className="pane-fact-label">{props.label}</dt>
      <dd className="pane-fact-value" title={props.value ?? undefined}>
        {props.value ?? <span className="pane-fact-empty">—</span>}
      </dd>
    </div>
  );
}

/** "Berlin, Germany", or whichever half the sheet has. */
function hqText(company: PortfolioCompanyDetail): string | null {
  const parts = [company.overview.hqCity, company.overview.country].filter(
    (part): part is string => part !== null,
  );
  return parts.length === 0 ? null : parts.join(', ');
}

/** "Seed · 2019": the stage as the app spells it, or the sheet's own word for a deal that is not one. */
function entryText(company: PortfolioCompanyDetail): string | null {
  const { entryStage, entryStageRaw, entryYear } = company.overview;
  const stage = entryStage === null ? entryStageRaw : STAGE_LABELS[entryStage];
  const parts = [stage, entryYear === null ? null : String(entryYear)].filter(
    (part): part is string => part !== null,
  );
  return parts.length === 0 ? null : parts.join(' · ');
}

/** "Led by Accel · Index, Kinnevik". */
function participantsText(round: PortfolioRound): string {
  const names = round.participants.map((row) => ({
    name: row.fundName ?? row.nameRaw,
    lead: row.lead,
  }));
  const leads = names.filter((row) => row.lead).map((row) => row.name);
  const rest = names.filter((row) => !row.lead).map((row) => row.name);
  return [leads.length > 0 ? `Led by ${leads.join(', ')}` : '', rest.join(', ')]
    .filter((part) => part !== '')
    .join(' · ');
}

function roundsCsv(company: PortfolioCompanyDetail): string {
  return toCsv(
    ['Company', 'Round', 'Amount', 'Lead', 'Participants', 'Source'],
    company.rounds.map((round) => [
      company.name,
      roundTitle(round),
      roundAmount(round),
      round.participants
        .filter((row) => row.lead)
        .map((row) => row.fundName ?? row.nameRaw)
        .join(', '),
      round.participants.map((row) => row.fundName ?? row.nameRaw).join(', '),
      round.source,
    ]),
  );
}

function slugOf(name: string): string {
  return (
    name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '') || 'company'
  );
}
